import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useNavigate } from "react-router-dom";

/**
 * PayPalCheckout — renders the PayPal buttons alongside Stripe on the cart.
 *
 * On mount, hits /api/paypal/config to find out if PayPal is enabled +
 * fetch the platform Client ID. If disabled, renders nothing.
 *
 * Flow:
 *   1. Buyer clicks PayPal button → PayPalButtons calls createOrder,
 *      which we forward to POST /api/paypal/orders → returns paypal_order_id
 *   2. Buyer completes approval flow → PayPalButtons calls onApprove,
 *      we forward to POST /api/paypal/orders/{id}/capture → funds captured
 *   3. On success, navigate to /orders (or /checkout/success)
 */
export default function PayPalCheckout({ onError, disabled }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/paypal/config`);
        if (!cancelled) setConfig(r.data);
      } catch {
        /* silent — PayPal is optional */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !config?.enabled || !config?.client_id) return null;

  return (
    <div
      className={`mt-3 ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      data-testid="paypal-checkout-wrapper"
    >
      <div className="text-center mb-2">
        <span className="text-xs uppercase tracking-widest text-gray-400 font-body">
          or pay with
        </span>
      </div>
      <PayPalScriptProvider
        options={{
          clientId: config.client_id,
          currency: "USD",
          intent: "capture",
        }}
      >
        <PayPalButtons
          style={{ layout: "horizontal", tagline: false, height: 45, shape: "pill" }}
          disabled={!!disabled}
          createOrder={async () => {
            try {
              const r = await axios.post(
                `${API}/paypal/orders`,
                { origin_url: window.location.origin },
                { withCredentials: true }
              );
              return r.data.paypal_order_id;
            } catch (e) {
              const msg = e.response?.data?.detail || "Couldn't start PayPal checkout";
              toast.error(msg);
              onError?.(msg);
              throw e;
            }
          }}
          onApprove={async (data) => {
            try {
              const r = await axios.post(
                `${API}/paypal/orders/${data.orderID}/capture`,
                {},
                { withCredentials: true }
              );
              toast.success("Payment complete!");
              navigate(`/orders?paid=${r.data.order_id}`);
            } catch (e) {
              const msg = e.response?.data?.detail || "Payment capture failed";
              toast.error(msg);
              onError?.(msg);
            }
          }}
          onError={(err) => {
            const msg = err?.message || "PayPal error";
            toast.error(msg);
            onError?.(msg);
          }}
          onCancel={() => {
            toast.info("PayPal checkout cancelled.");
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
}
